import { mapGetters } from 'vuex'
import { onMount } from '@/helpers/event'
import props from './sticky-menu-props'

export const SCROLL_DIRECTIONS = {
  down: 1,
  up: 2,
}

export default {
  props,

  data() {
    return {
      isInteractive: false,
      isScrolling: false,
      isScrollingDown: null,
      isStickingToBottom: false,
      hasWindowTallerThanMenu: false,
      spaceLeftBeforeStick: 1,
      parentLayout: null,
      parentMaxHeight: null,
      hasScrollEventListener: false,
      lastScrollY: 0,
      isMounted: false,
      preventScrollIfNeeded: () => false,
    }
  },

  computed: {
    ...mapGetters({
      viewportSize: 'viewport/dimensions',
    }),

    parentNode() {
      return this.isMounted ? this.$el.parentNode : null
    },

    headerHeight() {
      return this.isHeaderFixed ? document.querySelector('header')?.offsetHeight || 0 : 0
    },
  },

  watch: {
    isInteractive() {
      if (this.isInteractive && !this.hasScrollEventListener) {
        window.addEventListener('scroll', this.onScroll)
        this.hasScrollEventListener = true
      } else {
        window.removeEventListener('scroll', this.onScroll)
        this.hasScrollEventListener = false
      }
      this.setMenuPosition()
    },

    isActive: {
      immediate: true,
      handler() {
        this.isActive ? this.initInteraction() : this.killInteraction()
      },
    },

    viewportSize() {
      this.updateParentLayout()
      this.updateSpaceLeftBeforeStick()
      this.resetParentHeightIfNeeded()
      this.updateHasWindowTallerThanMenu()
    },

    spaceLeftBeforeStick(spaceLeft) {
      this.isStickingToBottom = spaceLeft <= 0
    },

    isStickingToBottom: {
      immediate: true,
      handler(value) {
        this.$emit('sticking', value)
        if (this.isActive) this.onStickingToBottomChange(value)
      },
    },
  },

  mounted() {
    this.isMounted = true
    this.$emit('mounted')
  },

  beforeDestroy() {
    this.killInteraction()
  },

  methods: {
    async initInteraction() {
      await onMount(this)
      if (!this.isActive) return

      this.updateParentLayout()
      this.updateSpaceLeftBeforeStick()
      this.isInteractive = true
    },

    killInteraction() {
      this.isInteractive = false
      if (this.isMounted) this.resetInitialPosition()
    },

    onScroll(event) {
      if (!!event && this.isScrolling) return
      if (this.preventScrollIfNeeded()) return

      const currentScrollY = this.getCurrentScrollY()
      const isScrolling = !!event || this.lastScrollY !== currentScrollY

      if (isScrolling) this.setMenuPosition(true)
      else this.isScrolling = false
    },

    scrollToTop() {
      if (!this.isActive || !this.parentLayout) return

      const currentScrollY = this.getCurrentScrollY()
      const pageY = this.hasWindowTallerThanMenu
        ? this.parentLayout.top - this.headerHeight
        : Math.round(Math.max(this.parentLayout.top - this.$el.getBoundingClientRect().top, 0))

      if (currentScrollY > pageY) window.scrollTo(0, pageY)
      this.isStickingToBottom = false
      this.resetInitialPosition()
    },

    disableScroll() {
      const currentScrollY = this.getCurrentScrollY()
      this.lastScrollY = currentScrollY

      this.preventScrollIfNeeded = () => {
        window.scrollTo(0, currentScrollY)
        return true
      }
    },

    enableScroll() {
      this.preventScrollIfNeeded = () => false
    },

    beforeHeightChange(additionalHeight, disablesScroll) {
      if (!additionalHeight || !this.isActive) return

      this.updateParentLayout()
      this.updateSpaceLeftBeforeStick()
      let isForcingUpPosition = additionalHeight > 0 && this.spaceLeftBeforeStick < additionalHeight

      if (!isForcingUpPosition && additionalHeight < 0) {
        const rect = this.$el.getBoundingClientRect()
        const belowFoldBottom = rect.top + rect.height - window.innerHeight
        isForcingUpPosition = belowFoldBottom + additionalHeight < 0
      }
      let newPosition
      let resetPosition = () => {}

      if (!this.isStickingToBottom && !this.hasWindowTallerThanMenu) {
        if (!this.isScrollingDown && !isForcingUpPosition) {
          newPosition = SCROLL_DIRECTIONS.down
          resetPosition = this.setScrollingDownPosition
        }
        if (this.isScrollingDown && isForcingUpPosition) {
          newPosition = SCROLL_DIRECTIONS.up
          resetPosition = this.setScrollingUpPosition
        }
      }
      this.updateHasReachedEdge(newPosition)
      resetPosition()

      if (disablesScroll) this.disableScroll()
    },

    getCurrentScrollY() {
      return Math.max(window.pageYOffset, 0)
    },

    updateHasWindowTallerThanMenu() {
      const offsetHeight = this.$el.offsetHeight
      const winHeight = window.innerHeight
      this.hasWindowTallerThanMenu = offsetHeight - winHeight + this.headerHeight <= 0
    },

    updateSpaceLeftBeforeStick() {
      this.spaceLeftBeforeStick =
        this.parentMaxHeight - this.$el.offsetTop - this.marginTop - this.$el.offsetHeight
    },

    updateParentLayout() {
      // It can happen that the parentNode is not yet displayed (offsetHeight would be zero) which
      // would break the parentLayout calculation + other calculations based on parentLayout values.
      // In that case, we return `null` which means it will try to call updateParentLayout again
      // when the user starts to scroll the visible parentNode.
      if (!this.parentNode.offsetHeight) return null

      const styleHeight = this.parentNode.style.height
      const styleMarginTop = this.parentNode.style.marginTop
      this.parentNode.style.height = ''
      this.parentNode.style.marginTop = ''
      this.parentMaxHeight = this.parentNode.offsetHeight

      const parentRect = this.parentNode.getBoundingClientRect()
      const top = parentRect.top + window.pageYOffset
      const bottom = parentRect.bottom + window.pageYOffset
      this.parentLayout = { top, bottom }

      this.parentNode.style.height = styleHeight
      this.parentNode.style.marginTop = styleMarginTop
    },

    resetParentHeightIfNeeded() {
      if (this.parentHeight && this.$el.offsetHeight >= this.parentHeight) this.parentHeight = ''
    },

    setMenuPosition(isScrolling = false) {
      if (!this.isActive) return

      if (!this.isScrolling || !this.parentLayout) this.updateParentLayout()
      if (!this.parentLayout) return // Wait until parentLayout is generated

      const currentScrollY = this.getCurrentScrollY()
      const isScrollingDown = this.isItScrollingDown()
      this.updateHasWindowTallerThanMenu()
      this.updateSpaceLeftBeforeStick()
      this.updateHasReachedEdge()

      if (this.isScrollingDown !== isScrollingDown || !this.supportsPositionSticky) {
        if (this.hasWindowTallerThanMenu) {
          this.setWindowTallerThanMenuPosition()
        } else if (isScrollingDown) {
          this.setScrollingDownPosition()
        } else {
          this.setScrollingUpPosition()
        }
      }
      if (isScrolling) {
        this.lastScrollY = currentScrollY
        requestAnimationFrame(() => this.onScroll(false))
      }
      this.isScrolling = isScrolling
    },
  },
}
